// src/components/lead-tracker/MapSourcesPanel.jsx
// Owner-only: map each TES lead source to one of the coach's 8 buckets.
// Saves trackerBucket straight onto the lead_sources doc (updateLeadSource
// passes the patch through), then onSaved refreshes the global source list.
import React, { useState } from "react";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import { updateLeadSource } from "../../utils/firebasefunctions";
import { TRACKER_BUCKETS } from "../../utils/leadTracker";

export default function MapSourcesPanel({ onClose, onSaved }) {
  const [{ leadsource }] = useStateValue();
  const [busyId, setBusyId] = useState("");
  const valid = (code) => TRACKER_BUCKETS.some((b) => b.code === code);
  const sources = (leadsource || [])
    .slice()
    .sort((a, b) => Number(valid(a.trackerBucket)) - Number(valid(b.trackerBucket)));

  const setBucket = async (src, code) => {
    const id = String(src.docId || src.id);
    setBusyId(id);
    try {
      const res = await updateLeadSource(id, { trackerBucket: code });
      if (res.success) {
        await onSaved();
      } else {
        toast.error(res.error || "Couldn't save mapping");
      }
    } catch (e) {
      // onSaved (the source-list refresh) can throw on a network failure;
      // without this the select stayed disabled forever.
      toast.error("Couldn't refresh the source list. Please reload the page.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/30 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-slate-900">Map Sources to Buckets</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Each TES lead source feeds one of your coach's 8 buckets. Unmapped
          sources show a warning on the tracker until you set them.
        </p>
        {sources.map((s) => {
          const id = String(s.docId || s.id);
          return (
            <div key={id} className="flex items-center justify-between py-2 border-t border-slate-100">
              <div className="text-sm font-semibold text-slate-800">
                {s.name || s.abbreviation || id}
                {!valid(s.trackerBucket) && (
                  <span className="ml-2 text-[10px] text-rose-600 font-bold">UNMAPPED ⚠</span>
                )}
              </div>
              <select
                disabled={busyId === id}
                className="text-xs border border-slate-300 rounded-lg px-2 py-1.5"
                value={valid(s.trackerBucket) ? s.trackerBucket : ""}
                onChange={(e) => setBucket(s, e.target.value)}
              >
                <option value="">Not mapped</option>
                {TRACKER_BUCKETS.map((b) => (
                  <option key={b.code} value={b.code}>
                    {b.code} - {b.label}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}
